import type { Firestore } from 'firebase-admin/firestore'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import { Timestamp } from 'firebase-admin/firestore'
import { createHash } from 'node:crypto'
import type {
  Plan,
  Stage,
  UserLocationSummary,
  UserLocationUpdateDTO,
  Location,
  RankedCity,
  TravelModeBreakdown,
  TagUsageEntry,
  RecentTripEntry,
  UserStatsAggregated,
  UserGamificationStats,
  UserStatsSnapshot
} from '@my-roadtrip/types'
import { calculateDistance } from '@my-roadtrip/utils/geo'
import { isLocationSource } from '@my-roadtrip/utils'
import { createLogger } from '../../common/utils/logger.js'
import type { FirestoreDoc, FirestoreSnapshot, FirestoreData } from '../../common/types/firestore.js'

const logger = createLogger('UsersRepository')

type ExtendedLocation = Location & {
  country?: string
  countryCode?: string
  city?: string
}

export class UsersRepository {
  private readonly plansCollection = 'plans'
  private readonly stagesCollection = 'stages'

  constructor(private readonly db: Firestore) {}

  async getUserStats(userId: string): Promise<{ stats: UserStatsAggregated; plans: Plan[]; signature: string }> {
    logger.debug('Fetching user stats', { userId })
    const plansSnapshot = await this.db
      .collection(this.plansCollection)
      .where('userId', '==', userId)
      .select(
        'name',
        'updatedAt',
        'createdAt',
        'routeData',
        'settings',
        'tags',
        'stats',
        'collaborators'
      )
      .get()
    const plans: Plan[] = plansSnapshot.docs.map((doc: FirestoreDoc) => ({
      ...(doc.data() as Plan),
      id: doc.id
    }))

    const tripsCreated = plans.length

    if (tripsCreated === 0) {
      const emptyStats: UserStatsAggregated = {
        tripsCreated: 0,
        kmDriven: 0,
        countriesVisited: 0,
        totalStages: 0,
        longestTripKm: 0,
        averageTripKm: 0,
        totalDriveHours: 0,
        averageStageDurationMinutes: 0,
        lastTripAt: undefined,
        topCountries: [],
        topCities: [],
        travelModes: [],
        tagUsage: [],
        recentTrips: []
      }

      return {
        stats: emptyStats,
        plans: [],
        signature: this.computePlansSignature([])
      }
    }

    const stageMap = await this.loadStagesForPlans(plans.map(plan => plan.id))

    let totalDistanceKm = 0
    let longestTripKm = 0
    let totalDurationMinutes = 0
    let totalStages = 0
    let lastTripAt: Date | undefined

    const countryFrequency = new Map<string, number>()
    const cityFrequency = new Map<string, RankedCity>()
    const travelModeFrequency = new Map<string, number>()
    const tagFrequency = new Map<string, TagUsageEntry>()

    const recentTrips: RecentTripEntry[] = []

    for (const plan of plans) {
      const stages = stageMap.get(plan.id) ?? []
      totalStages += stages.length

      const normalizedUpdatedAt = this.normalizeDate(plan.updatedAt) ?? this.normalizeDate(plan.createdAt)
      if (normalizedUpdatedAt && (!lastTripAt || normalizedUpdatedAt > lastTripAt)) {
        lastTripAt = normalizedUpdatedAt
      }

      const planDistanceKm = this.resolvePlanDistance(plan, stages)
      totalDistanceKm += planDistanceKm
      if (planDistanceKm > longestTripKm) {
        longestTripKm = planDistanceKm
      }

      const durationMinutes = this.resolvePlanDuration(plan, stages)
      totalDurationMinutes += durationMinutes

      recentTrips.push({
        id: plan.id,
        name: plan.name,
        updatedAt: (normalizedUpdatedAt ?? new Date()).toISOString(),
        distanceKm: Number(planDistanceKm.toFixed(1))
      })

      if (plan.tags) {
        for (const tag of plan.tags) {
          if (isNonEmptyString(tag)) {
            const key = tag.trim().toLowerCase()
            const entry = tagFrequency.get(key)
            if (entry) {
              entry.count += 1
            } else {
              tagFrequency.set(key, { tag: this.capitalizeLabel(tag.trim()), count: 1 })
            }
          }
        }
      }

      const mode = plan.settings?.vehicleType
      if (mode) {
        travelModeFrequency.set(mode, (travelModeFrequency.get(mode) ?? 0) + 1)
      }

      for (const stage of stages) {
        const { country, city } = this.extractLocationParts(stage)
        if (country) {
          countryFrequency.set(country, (countryFrequency.get(country) ?? 0) + 1)
        }
        if (city) {
          const key = `${city.toLowerCase()}|${country ?? ''}`
          const existing = cityFrequency.get(key)
          cityFrequency.set(key, {
            city,
            country: country ?? existing?.country,
            count: existing ? existing.count + 1 : 1
          })
        }
      }
    }

    recentTrips.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

    const topCountries = this.rankMap(countryFrequency, ({ key, value }) => ({ country: key, count: value }))
    const topCities = Array.from(cityFrequency.values()).sort((a, b) => b.count - a.count).slice(0, 5)
    const travelModes: TravelModeBreakdown[] = Array.from(travelModeFrequency.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([mode, count]) => ({ mode: mode as TravelModeBreakdown['mode'], count }))
    const tagUsage: TagUsageEntry[] = Array.from(tagFrequency.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)

    const stats: UserStatsAggregated = {
      tripsCreated,
      kmDriven: Math.round(totalDistanceKm),
      countriesVisited: countryFrequency.size,
      totalStages,
      longestTripKm: Math.round(longestTripKm),
      averageTripKm: tripsCreated > 0 ? Number((totalDistanceKm / tripsCreated).toFixed(1)) : 0,
      totalDriveHours: Number((totalDurationMinutes / 60).toFixed(1)),
      averageStageDurationMinutes: totalStages > 0 ? Math.round(totalDurationMinutes / totalStages) : 0,
      lastTripAt: lastTripAt?.toISOString(),
      topCountries,
      topCities,
      travelModes,
      tagUsage,
      recentTrips: recentTrips.slice(0, 5)
    }

    logger.info('User stats generated', {
      userId,
      tripsCreated,
      kmDriven: stats.kmDriven,
      countriesVisited: stats.countriesVisited
    })

    return { stats, plans, signature: this.computePlansSignature(plans) }
  }

  async persistAggregatedStats(
    userId: string,
    stats: UserStatsAggregated,
    plans?: Plan[],
    signature?: string,
    statsSnapshot?: UserStatsSnapshot
  ): Promise<void> {
    try {
      const userRef = this.db.collection('users').doc(userId)
      const existing = await userRef.get()

      const [sharedPlansCount, collaborationsCount] = await Promise.all<number>([
        this.countUserShares(userId),
        plans ? this.countCollaborationsFromPlans(plans, userId) : this.countUserCollaborations(userId)
      ])

      const nextStats: UserGamificationStats = {
        plansCreated: stats.tripsCreated,
        stagesCreated: stats.totalStages,
        totalDistance: Math.max(0, Math.round(stats.kmDriven * 1000)),
        totalDuration: Math.max(0, Math.round(stats.totalDriveHours * 3600)),
        countriesVisited: stats.countriesVisited,
        citiesVisited: stats.topCities.length,
        sharedPlans: sharedPlansCount,
        collaborations: collaborationsCount
      }

      const payload: FirestoreData = {
        gamification: {
          ...(existing.data()?.gamification ?? {}),
          stats: nextStats
        }
      }

      if (signature && statsSnapshot) {
        payload.statsSnapshot = statsSnapshot
        payload.statsSnapshotSignature = signature
        payload.statsSnapshotUpdatedAt = Timestamp.now()
      }

      await userRef.set(payload, { merge: true })

      logger.debug('Persisted aggregated stats to gamification profile', {
        userId,
        trips: stats.tripsCreated,
        kmDriven: stats.kmDriven,
        sharedPlans: sharedPlansCount,
        collaborations: collaborationsCount
      })
    } catch (error) {
      logger.warn('Failed to persist aggregated stats', {
        userId,
        error: (error as Error).message
      })
    }
  }

  async getPlanSignature(userId: string): Promise<{ signature: string; count: number }> {
    const snapshot = await this.db
      .collection(this.plansCollection)
      .where('userId', '==', userId)
      .select('updatedAt', 'createdAt')
      .get()
    const plans = snapshot.docs.map(doc => ({
      id: doc.id,
      updatedAt: (doc.data() as Plan).updatedAt,
      createdAt: (doc.data() as Plan).createdAt
    }))
    return {
      signature: this.computePlansSignature(plans),
      count: plans.length
    }
  }

  async getStoredStatsSnapshot(
    userId: string
  ): Promise<{ signature: string | null; snapshot: UserStatsSnapshot | null }> {
    const userDoc = await this.db.collection('users').doc(userId).get()
    if (!userDoc.exists) {
      return { signature: null, snapshot: null }
    }
    const data = userDoc.data() as FirestoreData
    const signature = typeof data.statsSnapshotSignature === 'string' ? data.statsSnapshotSignature : null
    const snapshot = data.statsSnapshot ? (data.statsSnapshot as UserStatsSnapshot) : null
    return { signature, snapshot }
  }

  private async countUserShares(userId: string): Promise<number> {
    const snapshot = await this.db.collection('shareLinks').where('createdBy', '==', userId).get()
    return snapshot.size
  }

  private async countUserCollaborations(userId: string): Promise<number> {
    const snapshot = await this.db.collection(this.plansCollection).where('userId', '==', userId).get()
    let total = 0

    for (const doc of snapshot.docs as FirestoreDoc[]) {
      total += this.countCollaborationsFromPlanData(doc.data() as Plan, userId)
    }

    return total
  }

  private countCollaborationsFromPlans(plans: Plan[], userId: string): number {
    return plans.reduce((acc, plan) => acc + this.countCollaborationsFromPlanData(plan, userId), 0)
  }

  private countCollaborationsFromPlanData(plan: Plan | { collaborators?: unknown }, userId: string): number {
    const collaboratorsRaw = Array.isArray(plan.collaborators) ? plan.collaborators : []
    let total = 0

    for (const collaborator of collaboratorsRaw) {
      if (isString(collaborator)) {
        if (collaborator && collaborator !== userId) {
          total += 1
        }
        continue
      }

      if (collaborator && typeof collaborator === 'object') {
        const collaboratorId = typeof (collaborator as { userId?: unknown }).userId === 'string'
          ? ((collaborator as { userId: string }).userId)
          : undefined

        if (!collaboratorId || collaboratorId === userId) {
          continue
        }

        total += 1
      }
    }

    return total
  }

  private computePlansSignature(plans: Array<Pick<Plan, 'id' | 'updatedAt' | 'createdAt'>>): string {
    if (!plans.length) {
      return ''
    }
    const hash = createHash('sha1')
    const entries = plans
      .map(plan => {
        const updated = this.normalizeDate(plan.updatedAt) ?? this.normalizeDate(plan.createdAt) ?? new Date(0)
        return `${plan.id}:${updated.getTime()}`
      })
      .sort()
      .join('|')
    hash.update(entries)
    return hash.digest('hex')
  }

  private async loadStagesForPlans(planIds: string[]): Promise<Map<string, Stage[]>> {
    const stageMap = new Map<string, Stage[]>()
    if (planIds.length === 0) {
      return stageMap
    }

    const chunkSize = 10
    const batches: Array<Promise<FirestoreSnapshot>> = []
    for (let i = 0; i < planIds.length; i += chunkSize) {
      const chunk = planIds.slice(i, i + chunkSize)
      batches.push(
        this.db
          .collection(this.stagesCollection)
          .where('planId', 'in', chunk)
          .select(
            'planId',
            'location',
            'order',
            'duration',
            'estimatedArrival',
            'estimatedDeparture',
            'day',
            'routeData'
          )
          .get()
      )
    }

    const snapshots = await Promise.all(batches)

    for (const snapshot of snapshots) {
      for (const doc of snapshot.docs as FirestoreDoc[]) {
        const data = doc.data() as Stage
        const planId = data.planId
        if (!planId) continue
        const bucket = stageMap.get(planId) ?? []
        bucket.push({ ...data, id: data.id ?? doc.id })
        stageMap.set(planId, bucket)
      }
    }

    for (const [planId, stages] of stageMap) {
      stages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      stageMap.set(planId, stages)
    }

    return stageMap
  }

  private resolvePlanDistance(plan: Plan, stages: Stage[]): number {
    if (plan.routeData?.distance && Number.isFinite(plan.routeData.distance)) {
      return Number(plan.routeData.distance)
    }

    const stats = (plan as unknown as { stats?: { totalDistance?: number } }).stats
    if (stats?.totalDistance && Number.isFinite(stats.totalDistance)) {
      return Number(stats.totalDistance)
    }

    if (stages.length < 2) {
      return 0
    }

    let distanceMeters = 0
    for (let i = 0; i < stages.length - 1; i++) {
      const from = this.normalizeLatLng(stages[i])
      const to = this.normalizeLatLng(stages[i + 1])
      if (!from || !to) continue
      distanceMeters += calculateDistance(from, to)
    }

    return Number((distanceMeters / 1000).toFixed(2))
  }

  private resolvePlanDuration(plan: Plan, stages: Stage[]): number {
    if (plan.routeData?.duration && Number.isFinite(plan.routeData.duration)) {
      return Number(plan.routeData.duration)
    }

    const stats = (plan as unknown as { stats?: { estimatedTime?: number } }).stats
    if (stats?.estimatedTime && Number.isFinite(stats.estimatedTime)) {
      return Number(stats.estimatedTime)
    }

    let total = 0
    for (const stage of stages) {
      if (typeof stage.duration === 'number' && Number.isFinite(stage.duration)) {
        total += stage.duration
        continue
      }

      const arrival = this.normalizeDate(stage.estimatedArrival)
      const departure = this.normalizeDate(stage.estimatedDeparture)
      if (arrival && departure) {
        const diffMinutes = Math.max(0, (arrival.getTime() - departure.getTime()) / 60000)
        if (Number.isFinite(diffMinutes)) {
          total += diffMinutes
        }
      }
    }

    return total
  }

  private normalizeLatLng(stage: Stage): { lat: number; lng: number } | null {
    const location = stage.location as ExtendedLocation | undefined
    if (!location) {
      return null
    }

    const latFallback = (location as unknown as { latitude?: unknown }).latitude
    const lngFallback = (location as unknown as { longitude?: unknown }).longitude
    const lat = typeof location.lat === 'number' ? location.lat : Number(latFallback)
    const lng = typeof location.lng === 'number' ? location.lng : Number(lngFallback)

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null
    }

    return { lat, lng }
  }

  private normalizeDate(value: unknown): Date | null {
    if (!value) return null
    if (value instanceof Date) return value
    if (isString(value)) {
      const parsed = new Date(value)
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
      const maybeToDate = (value as { toDate?: unknown }).toDate
      if (typeof maybeToDate === 'function') {
        const result = maybeToDate.call(value)
        return result instanceof Date ? result : null
      }
    }
    return null
  }

  private extractLocationParts(stage: Stage): { country?: string; city?: string } {
    const location = (stage.location as ExtendedLocation) ?? undefined
    if (!location) {
      return {}
    }

    const rawCountry = typeof location.country === 'string' ? location.country : undefined
    const countryCode = typeof location.countryCode === 'string' ? location.countryCode : undefined
    const address = typeof location.address === 'string' ? location.address : undefined

    const countryFromAddress = address ? this.pickFromAddress(address, 0) : undefined
    const cityFromAddress = address ? this.pickFromAddress(address, 1) : undefined

    const country = rawCountry ?? countryFromAddress ?? countryCode
    const city = cityFromAddress ?? (typeof location.city === 'string' ? location.city : undefined)

    return { country: country ? this.normalizeLabel(country) : undefined, city: city ? this.normalizeLabel(city) : undefined }
  }

  private pickFromAddress(address: string, offsetFromEnd: number): string | undefined {
    const parts = address
      .split(',')
      .map(part => part.trim())
      .filter(Boolean)

    if (parts.length === 0) {
      return undefined
    }

    const index = parts.length - 1 - offsetFromEnd
    if (index < 0 || index >= parts.length) {
      return undefined
    }

    return parts[index]
  }

  private normalizeLabel(label: string): string {
    return label.replace(/\s+/g, ' ').trim()
  }

  private capitalizeLabel(label: string): string {
    const normalized = this.normalizeLabel(label)
    if (!normalized) return normalized
    return normalized
      .split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  }

  private rankMap<T>(map: Map<string, number>, mapper: (entry: { key: string; value: number }) => T): T[] {
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([key, value]) => mapper({ key, value }))
  }

  private getUserDoc(userId: string) {
    return this.db.collection('users').doc(userId)
  }

  private getLocationSummaryDoc(userId: string) {
    return this.getUserDoc(userId).collection('tracking').doc('location')
  }

  private getLocationSamplesCollection(userId: string) {
    return this.getUserDoc(userId).collection('locationSamples')
  }

  private buildSummaryFromSnapshot(
    data: FirestoreData | undefined
  ): UserLocationSummary | null {
    if (!data) {
      return null
    }

    const lastUpdate = data.lastUpdate instanceof Timestamp ? data.lastUpdate.toDate() : undefined
    const sessionStartedAt =
      data.sessionStartedAt instanceof Timestamp ? data.sessionStartedAt.toDate() : undefined

    const lastPosition = data.lastPosition
      ? {
          lat: Number(data.lastPosition.lat ?? 0),
          lng: Number(data.lastPosition.lng ?? 0),
          accuracy:
            typeof data.lastPosition.accuracy === 'number'
              ? data.lastPosition.accuracy
              : undefined,
          altitude:
            typeof data.lastPosition.altitude === 'number'
              ? data.lastPosition.altitude
              : data.lastPosition.altitude ?? null,
          speed:
            typeof data.lastPosition.speed === 'number'
              ? data.lastPosition.speed
              : undefined,
          heading:
            typeof data.lastPosition.heading === 'number'
              ? data.lastPosition.heading
              : undefined,
          timestamp: lastUpdate ? lastUpdate.toISOString() : new Date().toISOString()
        }
      : undefined

    return {
      totalDistanceMeters: Number(data.totalDistanceMeters ?? 0),
      sessionDistanceMeters: Number(data.sessionDistanceMeters ?? 0),
      trackedDurationMs: Number(data.trackedDurationMs ?? 0),
      averageSpeedKmh: Number(data.averageSpeedKmh ?? 0),
      samples: Number(data.samples ?? 0),
      lastUpdate: lastUpdate?.toISOString(),
      sessionStartedAt: sessionStartedAt?.toISOString(),
      lastSource: isLocationSource(data.lastSource) ? data.lastSource : undefined,
      lastPosition,
    }
  }

  async saveUserLocation(
    userId: string,
    payload: UserLocationUpdateDTO
  ): Promise<UserLocationSummary> {
    logger.debug('Saving user location', { userId })
    const timestamp = payload.timestamp ? new Date(payload.timestamp) : new Date()
    const firestoreTimestamp = Timestamp.fromDate(timestamp)
    const summaryRef = this.getLocationSummaryDoc(userId)
    const samplesCollection = this.getLocationSamplesCollection(userId)

    const summary = await this.db.runTransaction(async (transaction) => {
      const existingSummarySnap = await transaction.get(summaryRef)
      const existingSummary = this.buildSummaryFromSnapshot(existingSummarySnap.data())

      const lastPosition = existingSummary?.lastPosition
      const lastUpdate = existingSummary?.lastUpdate ? new Date(existingSummary.lastUpdate) : undefined

      let distanceDelta = 0
      if (lastPosition) {
        distanceDelta = calculateDistance(
          { lat: lastPosition.lat, lng: lastPosition.lng },
          { lat: payload.latitude, lng: payload.longitude }
        )
        if (!Number.isFinite(distanceDelta) || distanceDelta < 1) {
          distanceDelta = 0
        }
      }

      const previousTotal = existingSummary?.totalDistanceMeters ?? 0
      const totalDistanceMeters = previousTotal + distanceDelta

      const previousDuration = existingSummary?.trackedDurationMs ?? 0
      const gapMs = lastUpdate ? Math.max(0, timestamp.getTime() - lastUpdate.getTime()) : 0
      const trackedDurationMs = previousDuration + (gapMs > 0 ? gapMs : 0)

      const sessionResetThresholdMs = 30 * 60 * 1000 // 30 minutes
      const previousSessionDistance = existingSummary?.sessionDistanceMeters ?? 0
      const previousSessionStartedAt = existingSummary?.sessionStartedAt
        ? new Date(existingSummary.sessionStartedAt)
        : undefined

      let sessionDistanceMeters = previousSessionDistance
      let sessionStartedAtTimestamp =
        previousSessionStartedAt ? Timestamp.fromDate(previousSessionStartedAt) : firestoreTimestamp

      if (!previousSessionStartedAt || (gapMs && gapMs > sessionResetThresholdMs)) {
        sessionDistanceMeters = distanceDelta
        sessionStartedAtTimestamp = firestoreTimestamp
      } else {
        sessionDistanceMeters += distanceDelta
      }

      const samples = (existingSummary?.samples ?? 0) + 1
      const speedFromPayload =
        typeof payload.speed === 'number' && payload.speed >= 0 ? payload.speed * 3.6 : undefined
      const averageSpeedKmh =
        trackedDurationMs > 0
          ? (totalDistanceMeters / trackedDurationMs) * 3.6
          : speedFromPayload ?? existingSummary?.averageSpeedKmh ?? 0

      const sampleRef = samplesCollection.doc()
      transaction.set(sampleRef, {
        timestamp: firestoreTimestamp,
        coordinates: {
          lat: payload.latitude,
          lng: payload.longitude,
          accuracy: payload.accuracy ?? null,
          altitude: payload.altitude ?? null,
          speed: payload.speed ?? null,
          heading: payload.heading ?? null
        },
        distanceDelta,
        source: payload.source ?? 'web'
      })

      transaction.set(
        summaryRef,
        {
          totalDistanceMeters,
          sessionDistanceMeters,
          trackedDurationMs,
          averageSpeedKmh,
          samples,
          lastUpdate: firestoreTimestamp,
          sessionStartedAt: sessionStartedAtTimestamp,
          lastSource: payload.source ?? 'web',
          lastPosition: {
            lat: payload.latitude,
            lng: payload.longitude,
            accuracy: payload.accuracy ?? null,
            altitude: payload.altitude ?? null,
            speed: payload.speed ?? null,
            heading: payload.heading ?? null
          }
        },
        { merge: true }
      )

      const sessionStartedAtIso = sessionStartedAtTimestamp.toDate().toISOString()
      const nextSummary: UserLocationSummary = {
        totalDistanceMeters,
        sessionDistanceMeters,
        trackedDurationMs,
        averageSpeedKmh,
        samples,
        lastUpdate: timestamp.toISOString(),
        sessionStartedAt: sessionStartedAtIso,
        lastSource: payload.source ?? 'web',
        lastPosition: {
          lat: payload.latitude,
          lng: payload.longitude,
          accuracy: payload.accuracy,
          altitude: payload.altitude ?? null,
          speed: payload.speed ?? null,
          heading: payload.heading ?? null,
          timestamp: timestamp.toISOString()
        }
      }

      return nextSummary
    })

    logger.info('User location saved', { userId })
    return summary
  }

  async getLocationSummary(userId: string): Promise<UserLocationSummary | null> {
    logger.debug('Fetching location summary', { userId })
    const summaryRef = this.getLocationSummaryDoc(userId)
    const snapshot = await summaryRef.get()
    const result = this.buildSummaryFromSnapshot(snapshot.data())
    logger.info('Location summary fetched', { userId, exists: !!result })
    return result
  }
}
